import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import IconDropdown from "../components/IconDropdown";

const ExamForm = ({ initialData, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    icon: '',
    color: '#4285F4',
    order: 0,
    isPurchasable: true,
    price: 0,
    discountPrice: '',
    testsOnlyPrice: 0,
    testsOnlyDiscountPrice: '',
    isFree: false,
    features: []
  });
  const [newFeature, setNewFeature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!initialData;

  // Function to generate URL-friendly slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        slug: initialData.slug || '',
        description: initialData.description || '',
        icon: initialData.icon || '',
        color: initialData.color || '#4285F4',
        order: initialData.order || 0,
        isPurchasable: initialData.isPurchasable !== undefined ? initialData.isPurchasable : true,
        price: initialData.price || 0,
        discountPrice: initialData.discountPrice || '',
        testsOnlyPrice: initialData.testsOnlyPrice || 0,
        testsOnlyDiscountPrice: initialData.testsOnlyDiscountPrice || '',
        isFree: initialData.isFree || false,
        features: initialData.features || []
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: isEdit ? prev.slug : generateSlug(value)
      }));
    } else if (type === 'checkbox') {
      if (name === 'isFree') {
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          // If exam is free, it shouldn't be purchasable and all prices should be 0
          isPurchasable: checked ? false : prev.isPurchasable,
          price: checked ? 0 : prev.price,
          discountPrice: checked ? '' : prev.discountPrice,
          testsOnlyPrice: checked ? 0 : prev.testsOnlyPrice,
          testsOnlyDiscountPrice: checked ? '' : prev.testsOnlyDiscountPrice
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : Number(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleIconChange = (iconValue) => {
    setFormData(prev => ({
      ...prev,
      icon: iconValue
    }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const validatePricing = () => {
    if (!formData.isFree && formData.isPurchasable) {
      if (!formData.price || formData.price <= 0) {
        alert('Full access price is required and must be greater than 0 for purchasable exams.');
        return false;
      }
      
      if (formData.discountPrice && formData.discountPrice >= formData.price) {
        alert('Full access discount price must be less than the regular full access price.');
        return false;
      }

      // Validate tests-only pricing if provided
      if (formData.testsOnlyPrice) {
        if (formData.testsOnlyPrice <= 0) {
          alert('Tests-only price must be greater than 0 if specified.');
          return false;
        }
        
        if (formData.testsOnlyPrice >= formData.price) {
          alert('Tests-only price must be less than full access price.');
          return false;
        }
        
        if (formData.testsOnlyDiscountPrice) {
          if (formData.testsOnlyDiscountPrice >= formData.testsOnlyPrice) {
            alert('Tests-only discount price must be less than the tests-only regular price.');
            return false;
          }
          
          if (formData.discountPrice && formData.testsOnlyDiscountPrice >= formData.discountPrice) {
            alert('Tests-only discount price must be less than full access discount price.');
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePricing()) {
      return;
    }
    
    setSubmitting(true);

    try {
      const result = await onSubmit(formData, isEdit);
      if (result?.success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5">
        <div className="modal-header">
          <h4 className="modal-title">{isEdit ? 'Edit Exam' : 'Create New Exam'}</h4>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="content-form">
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Title *</label>
                <input 
                  type="text" 
                  name="title" 
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Railway Recruitment Board"
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group half">
                <label className="form-label">Slug *</label>
                <input 
                  type="text" 
                  name="slug" 
                  value={formData.slug}
                  placeholder="Auto-generated from title"
                  className="form-input"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
                <small className="form-help-text">
                  {isEdit ? 'Kept stable after creation for URL use' : 'Auto-generated from title for URL use'}
                </small>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea 
                name="description" 
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the exam"
                className="form-textarea"
                rows="3"
              ></textarea>
            </div>
            
            {/* Features Section */}
            <div className="form-group">
              <label className="form-label">Exam Features</label>
              <small className="form-help-text" style={{ display: 'block', marginBottom: '10px' }}>
                Add features that will be displayed on the exam card (e.g., &quot;100+ Video Lessons&quot;, &quot;Study Materials & Notes&quot;)
              </small>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input 
                  type="text" 
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={handleFeatureKeyPress}
                  placeholder="Enter a feature (e.g., 100+ Video Lessons)"
                  className="form-input"
                  style={{ flex: 1 }}
                />
                <button 
                  type="button"
                  onClick={handleAddFeature}
                  className="btn-primary bg-blue"
                  style={{ 
                    padding: '8px 16px',
                    minWidth: '80px',
                    fontSize: '14px'
                  }}
                >
                  <i className="icon-plus" style={{ marginRight: '5px' }}></i>
                  Add
                </button>
              </div>
              
              {formData.features.length > 0 && (
                <div style={{ 
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#495057' }}>
                    Added Features ({formData.features.length}):
                  </div>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {formData.features.map((feature, index) => (
                      <li key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef'
                      }}>
                        <span style={{ 
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#495057'
                        }}>
                          <i className="icon-check" style={{ color: '#28a745', fontSize: '12px' }}></i>
                          {feature}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Remove feature"
                        >
                          <i className="icon-close"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {formData.features.length === 0 && (
                <div style={{ 
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '6px',
                  border: '1px solid #ffc107',
                  fontSize: '14px',
                  color: '#856404'
                }}>
                  <i className="icon-info-circle" style={{ marginRight: '5px' }}></i>
                  No features added yet. Add features to help users understand what&apos;s included in this exam.
                </div>
              )}
            </div>
            
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Icon</label>
                <IconDropdown 
                  value={formData.icon}
                  onChange={handleIconChange}
                  color={formData.color}
                />
              </div>
              
              <div className="form-group half">
                <label className="form-label">Color</label>
                <input 
                  type="color" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                  className="form-input color-input"
                />
              </div>
            </div>
            
            {/* Pricing Section */}
            <div className="form-section">
              <h5 className="form-section-title">Pricing Settings</h5>
              
              <div className="form-row">
                <div className="form-group half">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        name="isFree"
                        checked={formData.isFree}
                        onChange={handleInputChange}
                        className="form-checkbox"
                      />
                      <span className="checkbox-text">Free Exam</span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group half">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        name="isPurchasable"
                        checked={formData.isPurchasable}
                        onChange={handleInputChange}
                        className="form-checkbox"
                        disabled={formData.isFree}
                      />
                      <span className="checkbox-text">Available for Purchase</span>
                    </label>
                  </div>
                </div>
              </div>
              
              {!formData.isFree && formData.isPurchasable && (
                <>
                  <div className="form-row">
                    <div className="form-group half">
                      <label className="form-label">Full Access Price (₹) *</label>
                      <input 
                        type="number" 
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="e.g., 1999"
                        className="form-input"
                        min="0"
                        step="1"
                        required
                      />
                      <small className="form-help-text">Price for videos + tests + materials</small>
                    </div>
                    
                    <div className="form-group half">
                      <label className="form-label">Full Access Discount Price (₹)</label>
                      <input 
                        type="number" 
                        name="discountPrice"
                        value={formData.discountPrice}
                        onChange={handleInputChange}
                        placeholder="e.g., 1499"
                        className="form-input"
                        min="0"
                        step="1"
                      />
                      <small className="form-help-text">Optional. Must be less than full access price.</small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group half">
                      <label className="form-label">Tests-Only Price (₹)</label>
                      <input 
                        type="number" 
                        name="testsOnlyPrice"
                        value={formData.testsOnlyPrice}
                        onChange={handleInputChange}
                        placeholder="e.g., 999"
                        className="form-input"
                        min="0"
                        step="1"
                      />
                      <small className="form-help-text">Price for tests only (no videos/materials)</small>
                    </div>
                    
                    <div className="form-group half">
                      <label className="form-label">Tests-Only Discount Price (₹)</label>
                      <input 
                        type="number" 
                        name="testsOnlyDiscountPrice"
                        value={formData.testsOnlyDiscountPrice}
                        onChange={handleInputChange}
                        placeholder="e.g., 799"
                        className="form-input"
                        min="0"
                        step="1"
                      />
                      <small className="form-help-text">Optional. Must be less than tests-only price.</small>
                    </div>
                  </div>
                </>
              )}
              
              {formData.isFree && (
                <div className="form-note">
                  <i className="icon-info-circle"></i>
                  <span>This exam will be available to all users at no cost.</span>
                </div>
              )}
              
              {!formData.isFree && !formData.isPurchasable && (
                <div className="form-note warning">
                  <i className="icon-warning"></i>
                  <span>This exam will not be available for purchase or free access. Users will not be able to access its content.</span>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-primary bg-purple"
              >
                {submitting ? 'Saving...' : (isEdit ? 'Update Exam' : 'Create Exam')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

ExamForm.propTypes = {
  initialData: PropTypes.shape({
    title: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
    color: PropTypes.string,
    order: PropTypes.number,
    isPurchasable: PropTypes.bool,
    price: PropTypes.number,
    discountPrice: PropTypes.number,
    testsOnlyPrice: PropTypes.number,
    testsOnlyDiscountPrice: PropTypes.number,
    isFree: PropTypes.bool,
    features: PropTypes.arrayOf(PropTypes.string)
  }),
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ExamForm; 